import React from 'react';
import { Link } from 'react-router-dom';
import './Terms.css';

const Terms: React.FC = () => {
  return (
    <div className="terms-container">
      <div className="terms-content">
        <header className="terms-header">
          <Link to="/" className="back-link">← Back to Home</Link>
          <h1>Terms and Conditions</h1>
          <p className="last-updated">Last Updated: December 16, 2024</p>
        </header>

        <section className="terms-section">
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing and booking through Academic Loft ("the Property", "we", "us", or "our")
            at theacademicloft.com, you ("Guest", "you", or "your") accept and agree to be bound
            by these Terms and Conditions. If you do not agree to these terms, please do not use
            our booking service.
          </p>
        </section>

        <section className="terms-section">
          <h2>2. Booking and Reservations</h2>
          <h3>2.1 Booking Process</h3>
          <p>
            All bookings are subject to availability and confirmation. A booking is confirmed only
            when you receive a confirmation email from us after successful payment processing.
          </p>

          <h3>2.2 Payment</h3>
          <p>
            Full payment is required at the time of booking. We accept payments through Stripe,
            our secure payment processor. All prices are in USD and include applicable taxes unless
            otherwise stated.
          </p>

          <h3>2.3 Minimum Stay</h3>
          <p>
            Minimum stay requirements vary by date and season. The minimum stay requirement for
            your selected dates will be displayed during the booking process.
          </p>
        </section>

        <section className="terms-section">
          <h2>3. Cancellation Policy - Moderate</h2>
          <h3>3.1 Guest Cancellations</h3>
          <p>
            We offer a moderate cancellation policy that balances flexibility with protection
            for both guests and hosts. Cancellations must be made in writing via email to
            support@theacademicloft.com.
          </p>
          <p>
            <strong>Moderate Cancellation Policy:</strong>
          </p>
          <ul>
            <li><strong>Cancel 5 or more days before check-in:</strong> Full refund (100%)</li>
            <li><strong>Cancel less than 5 days before check-in:</strong> 50% refund of the total booking amount</li>
            <li><strong>No-show or early checkout:</strong> No refund for unused nights</li>
          </ul>
          <p>
            <em>Note: If you cancel within 48 hours of booking and the booking is made at least
            14 days before check-in, you'll receive a full refund.</em>
          </p>

          <h3>3.2 Host Cancellations</h3>
          <p>
            In the unlikely event that we need to cancel your reservation, you will receive a
            full refund. We are not responsible for any additional costs you may incur as a
            result of the cancellation.
          </p>
        </section>

        <section className="terms-section">
          <h2>4. Check-In and Check-Out</h2>
          <h3>4.1 Check-In Time</h3>
          <p>
            Standard check-in time is 3:00 PM. Early check-in may be available upon request
            and subject to availability. Check-in instructions will be sent to you 24 hours
            before your arrival date.
          </p>

          <h3>4.2 Check-Out Time</h3>
          <p>
            Standard check-out time is 11:00 AM. Late check-out may be available upon request
            and subject to additional fees and availability.
          </p>

          <h3>4.3 Identification</h3>
          <p>
            Guests must be at least 18 years old to book. Valid government-issued photo
            identification may be required upon request.
          </p>
        </section>

        <section className="terms-section">
          <h2>5. House Rules</h2>
          <p>
            Guests must comply with all house rules as outlined on our
            <Link to="/house-rules"> House Rules page</Link>. Violation of house rules may
            result in immediate termination of the reservation without refund and additional
            charges for damages.
          </p>
        </section>

        <section className="terms-section">
          <h2>6. Guest Responsibilities</h2>
          <h3>6.1 Property Care</h3>
          <p>
            Guests are responsible for treating the property with care and respect. Any damage
            beyond normal wear and tear will be charged to the guest.
          </p>

          <h3>6.2 Maximum Occupancy</h3>
          <p>
            Maximum occupancy is 5 guests. Exceeding the maximum occupancy without prior approval
            may result in additional charges or immediate termination of the reservation.
          </p>

          <h3>6.3 Noise and Disturbances</h3>
          <p>
            The property is located in a residential area. Guests must respect quiet hours
            (10:00 PM - 8:00 AM) and avoid creating disturbances to neighbors.
          </p>
        </section>

        <section className="terms-section">
          <h2>7. Liability and Damages</h2>
          <h3>7.1 Property Damage</h3>
          <p>
            Guests are liable for any damage caused to the property during their stay.
            We reserve the right to charge for repairs, replacements, or extra cleaning
            as necessary.
          </p>

          <h3>7.2 Personal Property</h3>
          <p>
            We are not responsible for any loss, theft, or damage to guests' personal
            belongings. Guests are advised to secure their valuables.
          </p>

          <h3>7.3 Limitation of Liability</h3>
          <p>
            To the maximum extent permitted by law, we shall not be liable for any indirect,
            incidental, special, consequential, or punitive damages arising out of or relating
            to your use of the property.
          </p>
        </section>

        <section className="terms-section">
          <h2>8. Force Majeure</h2>
          <p>
            We shall not be held liable for any failure or delay in performing our obligations
            due to circumstances beyond our reasonable control, including but not limited to
            natural disasters, government actions, pandemics, or utility failures.
          </p>
        </section>

        <section className="terms-section">
          <h2>9. Privacy and Data Protection</h2>
          <p>
            We collect and process personal information in accordance with applicable privacy
            laws. Your information is used solely for booking purposes and communication
            related to your reservation. We do not share your personal information with third
            parties except as necessary to process your booking (e.g., payment processor).
          </p>
        </section>

        <section className="terms-section">
          <h2>10. Modifications to Terms</h2>
          <p>
            We reserve the right to modify these Terms and Conditions at any time. Changes
            will be effective immediately upon posting to our website. Your continued use of
            our booking service after changes are posted constitutes acceptance of the modified
            terms.
          </p>
        </section>

        <section className="terms-section">
          <h2>11. Disputes and Governing Law</h2>
          <h3>11.1 Governing Law</h3>
          <p>
            These Terms and Conditions shall be governed by and construed in accordance with
            the laws of the State of California, United States, without regard to its conflict
            of law provisions.
          </p>

          <h3>11.2 Dispute Resolution</h3>
          <p>
            Any disputes arising from these terms or your use of the property shall be resolved
            through good faith negotiation. If negotiation fails, disputes shall be resolved
            through binding arbitration in Riverside County, California.
          </p>
        </section>

        <section className="terms-section">
          <h2>12. Contact Information</h2>
          <p>
            For questions about these Terms and Conditions, please contact us:
          </p>
          <ul className="contact-list">
            <li><strong>Email:</strong> support@theacademicloft.com</li>
            <li><strong>Phone:</strong> (949) 478-4034</li>
          </ul>
        </section>

        <section className="terms-section">
          <h2>13. Entire Agreement</h2>
          <p>
            These Terms and Conditions, together with our House Rules and any other policies
            posted on our website, constitute the entire agreement between you and Academic Loft
            regarding your use of our booking service and property.
          </p>
        </section>

        <footer className="terms-footer">
          <p>
            By completing a booking, you acknowledge that you have read, understood, and agree
            to be bound by these Terms and Conditions.
          </p>
          <Link to="/" className="back-button">Return to Home</Link>
        </footer>
      </div>
    </div>
  );
};

export default Terms;
